import React from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ListingTypeSection } from './ListingTypeSection';
import DatabaseDrivenFilters from '../DatabaseDrivenFilters';
import { MarketplaceSearchInput } from './MarketplaceSearchInput';
import { SortDropdown } from './SortDropdown';
import { X } from 'lucide-react';
import { JOB_CATEGORY_OPTIONS } from '@/types/jobWizard';
import { SERVICE_CATEGORY_CONFIG, ServiceCategory } from '@/utils/serviceTypeIcons';
import { cn } from '@/lib/utils';

interface MobileFilterDrawerProps {
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  sellListingType: string | null;
  buyRequestType: string | null;
  onSellTypeChange: (type: string | null) => void;
  onBuyTypeChange: (type: string | null) => void;
  partsListingType?: string | null;
  onPartsTypeChange?: (type: string | null) => void;
  manufacturers: string[];
  selectedManufacturers: string[];
  manufacturerCounts: Record<string, number>;
  onManufacturersChange: (manufacturers: string[]) => void;
  filters: any;
  onFiltersChange: (filters: any) => void;
  onClearFilters: () => void;
  marketMode?: 'all' | 'sell' | 'buy';
  onMarketModeChange?: (mode: 'all' | 'sell' | 'buy') => void;
}

export const MobileFilterDrawer: React.FC<MobileFilterDrawerProps> = ({
  assetType,
  open,
  onOpenChange,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  sellListingType,
  buyRequestType,
  onSellTypeChange,
  onBuyTypeChange,
  partsListingType,
  onPartsTypeChange,
  manufacturers,
  selectedManufacturers,
  manufacturerCounts,
  onManufacturersChange,
  filters,
  onFiltersChange,
  onClearFilters,
  marketMode = 'all',
  onMarketModeChange
}) => {
  // Dynamic options based on asset type
  const sellOptions = assetType === 'helicopter' 
    ? [
        { id: 'sale', label: 'For Sale', value: 'sale' },
        { id: 'wet_lease', label: 'Wet Lease', value: 'wet_lease' },
        { id: 'dry_lease', label: 'Dry Lease', value: 'dry_lease' },
      ]
    : [
        { id: 'engine_sell', label: 'For Sale', value: 'engine_sell' },
        { id: 'engine_rent', label: 'For Rent', value: 'engine_rent' },
      ];

  const buyOptions = assetType === 'helicopter'
    ? [
        { id: 'purchase', label: 'Purchase', value: 'buy_sale' },
        { id: 'wet_lease', label: 'Wet Lease', value: 'buy_wet_lease' },
        { id: 'dry_lease', label: 'Dry Lease', value: 'buy_dry_lease' },
      ]
    : [
        { id: 'engine_buy', label: 'Buy Request', value: 'engine_buy' },
        { id: 'engine_rent_request', label: 'Rent Request', value: 'engine_rent_request' },
      ];

  const sellTitle = assetType === 'helicopter' ? 'For Sale' : 'Engines for Sale';
  const buyTitle = assetType === 'helicopter' ? 'To Buy' : 'Engines to Buy';

  const handleSellTypeSelect = (type: string | null) => {
    onSellTypeChange(type);
    if (type !== null) {
      onBuyTypeChange(null);
      onMarketModeChange?.('sell');
    } else {
      onMarketModeChange?.('all');
    }
  };

  const handleBuyTypeSelect = (type: string | null) => {
    onBuyTypeChange(type);
    if (type !== null) {
      onSellTypeChange(null);
      onMarketModeChange?.('buy');
    } else {
      onMarketModeChange?.('all');
    }
  };

  const handlePartsTypeSelect = (type: string | null) => {
    if (onPartsTypeChange) {
      onPartsTypeChange(type);
      if (type === null) {
        onMarketModeChange?.('all');
      }
    }
  };

  const handleSellLabelClick = () => {
    if (marketMode === 'sell') {
      onMarketModeChange?.('all');
      onSellTypeChange(null);
    } else {
      onMarketModeChange?.('sell');
      onSellTypeChange(null);
      onBuyTypeChange(null);
    }
  };

  const handleBuyLabelClick = () => {
    if (marketMode === 'buy') {
      onMarketModeChange?.('all');
      onBuyTypeChange(null);
    } else {
      onMarketModeChange?.('buy');
      onBuyTypeChange(null);
      onSellTypeChange(null);
    }
  };

  const handleManufacturerToggle = (manufacturer: string) => {
    const newSelection = selectedManufacturers.includes(manufacturer)
      ? selectedManufacturers.filter(m => m !== manufacturer)
      : [...selectedManufacturers, manufacturer];
    onManufacturersChange(newSelection);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[92vh] p-0 flex flex-col gap-0">
        <SheetHeader className="px-4 py-3 border-b flex-shrink-0 bg-background">
          <SheetTitle className="text-base font-bold text-left">Filters & Search</SheetTitle>
          <SheetDescription className="sr-only">Filter and search marketplace listings</SheetDescription>
        </SheetHeader>

        {/* Search and Sort - clean, no backdrop blur */}
        <div className="px-4 py-3 border-b space-y-2 flex-shrink-0 bg-background">
          <MarketplaceSearchInput
            assetType={assetType}
            value={searchQuery}
            onChange={onSearchChange}
            disableSuggestions
          />
          <SortDropdown
            assetType={assetType}
            value={sortBy}
            onChange={onSortChange}
          />
        </div>

        <Tabs defaultValue="type" className="flex-1 flex flex-col min-h-0">
          <TabsList className={cn(
            "w-full grid grid-cols-3 border-b rounded-none flex-shrink-0 px-2 py-1.5 h-auto bg-muted/40"
          )}>
            <TabsTrigger value="type" className="rounded-md h-9 text-sm">Type</TabsTrigger>
            {assetType !== 'jobs' && assetType !== 'services' && (
              <TabsTrigger value="manufacturer" className="rounded-md h-9 text-sm">Manufacturer</TabsTrigger>
            )}
            {(assetType === 'jobs' || assetType === 'services') && (
              <TabsTrigger value="category" className="rounded-md h-9 text-sm">Category</TabsTrigger>
            )}
            <TabsTrigger value="more" className="rounded-md h-9 text-sm">More</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-hidden">
            <TabsContent value="type" className="h-full m-0">
              <ScrollArea className="h-full">
              <div className="p-4 space-y-6">
                  {assetType === 'parts' ? (
                    <ListingTypeSection
                      title="Parts & Tools Listings"
                      options={[
                        { id: 'parts_sell_aircraft', value: 'parts_sell_aircraft', label: 'Sell Aircraft Parts', count: 0 },
                        { id: 'parts_sell_engine', value: 'parts_sell_engine', label: 'Sell Engine Parts', count: 0 },
                        { id: 'parts_sell_general', value: 'parts_sell_general', label: 'Sell General Parts', count: 0 },
                        { id: 'parts_rent', value: 'parts_rent', label: 'Rent Out', count: 0 },
                        { id: 'parts_buy_aircraft', value: 'parts_buy_aircraft', label: 'Buy Aircraft Parts', count: 0 },
                        { id: 'parts_buy_engine', value: 'parts_buy_engine', label: 'Buy Engine Parts', count: 0 },
                        { id: 'parts_buy_general', value: 'parts_buy_general', label: 'Buy General Parts', count: 0 },
                        { id: 'parts_rent_request', value: 'parts_rent_request', label: 'Rent Request', count: 0 },
                      ]}
                      selectedValue={partsListingType || null}
                      onSelect={handlePartsTypeSelect}
                      variant="sell"
                    />
                  ) : assetType === 'jobs' ? (
                    <>
                      <ListingTypeSection
                        title="Companies Hiring"
                        options={[
                          { id: 'job_post', value: 'job_post', label: 'Job Postings' },
                          { id: 'job_contract', value: 'job_contract', label: 'Contract Work' },
                        ]}
                        selectedValue={sellListingType}
                        onSelect={handleSellTypeSelect}
                        variant="sell"
                        assetType="jobs"
                        onLabelClick={handleSellLabelClick}
                        isLabelActive={marketMode === 'sell'}
                      />
                      {/* Enhanced divider */}
                      <div className="h-px w-full bg-border" />
                      <ListingTypeSection
                        title="Professionals Looking"
                        options={[
                          { id: 'job_profile', value: 'job_profile', label: 'Professional Profiles' },
                          { id: 'job_seeking', value: 'job_seeking', label: 'Actively Seeking' },
                        ]}
                        selectedValue={buyRequestType}
                        onSelect={handleBuyTypeSelect}
                        variant="buy"
                        assetType="jobs"
                        onLabelClick={handleBuyLabelClick}
                        isLabelActive={marketMode === 'buy'}
                      />
                    </>
                  ) : assetType === 'services' ? (
                    <>
                      <ListingTypeSection
                        title="Service Providers"
                        options={[
                          { id: 'service_offer', value: 'service_offer', label: 'Service Offerings' },
                          { id: 'capability_listing', value: 'capability_listing', label: 'Capabilities' },
                        ]}
                        selectedValue={sellListingType}
                        onSelect={handleSellTypeSelect}
                        variant="sell"
                        assetType="services"
                        onLabelClick={handleSellLabelClick}
                        isLabelActive={marketMode === 'sell'}
                      />
                      {/* Enhanced divider */}
                      <div className="h-px w-full bg-border" />
                      <ListingTypeSection
                        title="Service Seekers"
                        options={[
                          { id: 'service_request', value: 'service_request', label: 'Service Requests' },
                          { id: 'rfq_request', value: 'rfq_request', label: 'RFQ Requests' },
                        ]}
                        selectedValue={buyRequestType}
                        onSelect={handleBuyTypeSelect}
                        variant="buy"
                        assetType="services"
                        onLabelClick={handleBuyLabelClick}
                        isLabelActive={marketMode === 'buy'}
                      />
                    </>
                  ) : (
                    <>
                      <ListingTypeSection
                        title={assetType === 'engine' ? 'Engines for Sale' : 'Helicopters for Sale'}
                        options={sellOptions}
                        selectedValue={sellListingType}
                        onSelect={handleSellTypeSelect}
                        variant="sell"
                        assetType={assetType}
                        onLabelClick={handleSellLabelClick}
                        isLabelActive={marketMode === 'sell'}
                      />
                      {/* Enhanced divider */}
                      <div className="h-px w-full bg-border" />
                      <ListingTypeSection
                        title={assetType === 'engine' ? 'Engines to Buy' : 'Helicopters to Buy'}
                        options={buyOptions}
                        selectedValue={buyRequestType}
                        onSelect={handleBuyTypeSelect}
                        variant="buy"
                        assetType={assetType}
                        onLabelClick={handleBuyLabelClick}
                        isLabelActive={marketMode === 'buy'}
                      />
                    </>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Manufacturer Tab - Only for Heli/Engine/Parts */}
            {assetType !== 'jobs' && assetType !== 'services' && (
              <TabsContent value="manufacturer" className="h-full m-0">
                <ScrollArea className="h-full">
                  <div className="p-4">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Select Manufacturers
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {manufacturers.map((manufacturer) => {
                        const isSelected = selectedManufacturers.includes(manufacturer);
                        const count = manufacturerCounts[manufacturer] || 0;
                        
                        return (
                          <Button
                            key={manufacturer}
                            variant={isSelected ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handleManufacturerToggle(manufacturer)}
                            className="justify-between h-10 px-3 w-full"
                          >
                            <span className="truncate text-left">{manufacturer}</span>
                            <Badge 
                              variant="secondary" 
                              className="ml-2 text-[10px] flex-shrink-0"
                            >
                              {count}
                            </Badge>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>
            )}

            {/* Category Tab - Jobs & Services Only */}
            {(assetType === 'jobs' || assetType === 'services') && (
              <TabsContent value="category" className="h-full m-0">
                <ScrollArea className="h-full">
                  <div className="p-4 space-y-4">
                    {assetType === 'jobs' ? (
                      <>
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                          Select Job Categories
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {JOB_CATEGORY_OPTIONS.map((cat) => {
                            const isSelected = (filters.jobCategories || []).includes(cat.value);
                            return (
                              <Button
                                key={cat.value}
                                variant={isSelected ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  const current = filters.jobCategories || [];
                                  const updated = current.includes(cat.value) 
                                    ? current.filter((c: string) => c !== cat.value) 
                                    : [...current, cat.value];
                                  onFiltersChange({ ...filters, jobCategories: updated });
                                }}
                                className="h-10 px-3 w-full justify-start"
                              >
                                <span className="truncate text-left">{cat.shortLabel}</span>
                              </Button>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                          Select Service Categories
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {(Object.entries(SERVICE_CATEGORY_CONFIG) as [ServiceCategory, { label: string; shortLabel: string }][]).map(([catId, catConfig]) => {
                            const isSelected = (filters.serviceCategories || []).includes(catId);
                            return (
                              <Button
                                key={catId}
                                variant={isSelected ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                  const current = filters.serviceCategories || [];
                                  const updated = current.includes(catId) 
                                    ? current.filter((c: string) => c !== catId) 
                                    : [...current, catId];
                                  onFiltersChange({ ...filters, serviceCategories: updated });
                                }}
                                className="h-10 px-3 w-full justify-start"
                              >
                                <span className="truncate text-left">{catConfig.shortLabel}</span>
                              </Button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
            )}

            <TabsContent value="more" className="h-full m-0">
              <ScrollArea className="h-full">
                <div className="p-4">
                  <DatabaseDrivenFilters
                    assetType={assetType}
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                  />
                </div>
              </ScrollArea>
            </TabsContent>
          </div>

          <div className="border-t p-4 flex gap-3 flex-shrink-0 bg-background">
            <Button 
              variant="outline" 
              onClick={onClearFilters} 
              className="flex-1 min-w-0"
            >
              Clear All
            </Button>
            <Button 
              onClick={() => onOpenChange(false)} 
              className="flex-1 min-w-0"
            >
              Apply Filters
            </Button>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
